import React, { useState, useEffect, useCallback } from 'react';
import { rentalApi } from '../services/api';
import { FairManagerPayoutsResponse } from '../types';
import { Download, DollarSign, Calendar, User, Mail, ChevronLeft, ChevronRight, AlertCircle, RefreshCw } from 'lucide-react';
import { showInfo } from '../utils/toast';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { extractApiError } from '../utils/errorUtils';

const FairManagerPayouts: React.FC = () => {
  const [payouts, setPayouts] = useState<FairManagerPayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedFairId] = useState<number | undefined>(undefined);

  const loadPayouts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rentalApi.getPayouts({
        fair_id: selectedFairId,
        page,
        page_size: pageSize
      });
      setPayouts(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load payouts:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [page, selectedFairId, pageSize]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  // Issue #905: formatCurrency now imported from utils/currency for Decimal.js support

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      refunded: 'bg-red-100 text-red-800 border-red-300',
      confirmed: 'bg-moonbone text-ink border-teal-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded border ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const exportToCSV = () => {
    if (!payouts || !payouts.items || payouts.items.length === 0) {
      showInfo('No data to export');
      return;
    }

    // Create CSV header
    const headers = [
      'Reservation ID',
      'Vendor Name',
      'Vendor Email',
      'Fair Name',
      'Slot Number',
      'Reservation Date',
      'Payment Date',
      'Gross Amount',
      'Commission Amount',
      'Net Amount',
      'Payment Status',
      'Reservation Status'
    ];

    // Create CSV rows - Issue #905: Currency values are already strings with 2 decimals
    const rows = payouts.items.map(item => [
      item.reservation_id,
      item.vendor_name,
      item.vendor_email,
      item.fair_name,
      item.slot_number,
      formatDate(item.reservation_date),
      item.payment_date ? formatDate(item.payment_date) : 'N/A',
      item.gross_amount, // Already string with 2 decimals (Issue #905)
      item.commission_amount, // Already string with 2 decimals (Issue #905)
      item.net_amount, // Already string with 2 decimals (Issue #905)
      item.payment_status,
      item.reservation_status
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payouts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !payouts) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !payouts) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={loadPayouts}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!payouts) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payout History</h1>
            <p className="mt-2 text-gray-600">
              Detailed commission breakdown for all reservations
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={payouts.items.length === 0}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-5 w-5 mr-2" />
            Export to CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Total Gross</h3>
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(payouts.total_gross)}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Total Commission</h3>
            <DollarSign className="h-6 w-6 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(payouts.total_commission)}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-dusty-teal">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Total Net</h3>
            <DollarSign className="h-6 w-6 text-secondary" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(payouts.total_net)}</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={loadPayouts}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            Showing {payouts.items.length} of {payouts.total} payouts
          </span>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {payouts.items.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500">No payout history yet</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reservation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fair / Slot
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gross
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payouts.items.map((item) => (
                    <tr key={item.reservation_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{item.reservation_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.vendor_name}</div>
                        <div className="text-sm text-gray-500">{item.vendor_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.fair_name}</div>
                        <div className="text-sm text-gray-500">Slot: {item.slot_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.reservation_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(item.gross_amount)} {/* Issue #905: Decimal.js formatting */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-orange-600">
                        {formatCurrency(item.commission_amount)} {/* Issue #905: Decimal.js formatting */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                        {formatCurrency(item.net_amount)} {/* Issue #905: Decimal.js formatting */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getStatusBadge(item.payment_status)}
                          {getStatusBadge(item.reservation_status)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {payouts.items.map((item) => (
                <div key={item.reservation_id} className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      Reservation #{item.reservation_id}
                    </span>
                    <div className="space-x-1">
                      {getStatusBadge(item.payment_status)}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="font-medium">{item.vendor_name}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      {item.vendor_email}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      {formatDate(item.reservation_date)}
                    </div>
                  </div>

                  <div className="border-t pt-3 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-gray-500">Gross</p>
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.gross_amount)} {/* Issue #905: Decimal.js formatting */}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Commission</p>
                      <p className="text-sm font-semibold text-orange-600">{formatCurrency(item.commission_amount)} {/* Issue #905: Decimal.js formatting */}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Net</p>
                      <p className="text-sm font-semibold text-green-600">{formatCurrency(item.net_amount)} {/* Issue #905: Decimal.js formatting */}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {payouts.total_pages > 1 && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(payouts.total_pages, p + 1))}
                disabled={page === payouts.total_pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{page}</span> of{' '}
                  <span className="font-medium">{payouts.total_pages}</span>
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(payouts.total_pages, p + 1))}
                  disabled={page === payouts.total_pages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FairManagerPayouts;
